import React, { FC } from 'react';
import { GDSIconSource, Icon } from '#components/icon/icon.tsx';
import { StackLayout } from '#components/stack-layout/stack-layout.tsx';
import { StackItem } from '#components/stack-layout/stack-item/stack-item.tsx';
import type { GDSCustomizableComponent } from '../../types';
import { GDSColor, styled } from '../../theme';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export type GDSTagCategory =
  | 'one'
  | 'two'
  | 'three'
  | 'four'
  | 'neutral'
  | 'success'
  | 'critical'
  | 'warning'
  | 'informational';

export interface GDSTagProps extends GDSCustomizableComponent {
  label: string;
  category?: GDSTagCategory;
  emphasis?: 'bold' | 'subdued';
  icon?: GDSIconSource;
  iconTint?: GDSColor;
  size?: 'small' | 'medium';
  iconAltText?: string;
}

const DISPLAY_NAME = 'Tag';

const BaseTag = styled(StackLayout, {
  borderRadius: '$tags',
  borderStyle: 'solid',
  borderWidth: '$borderWidths$thin',
  display: 'inline-flex',
  variants: {
    size: {
      small: {
        typography: '$label',
        padding: '$eighth $quarter',
      },
      medium: {
        typography: '$bodyTwo',
        padding: '$quarter $half',
      },
    },
    // Empty variant definitions to allow for adding compound variant styles below
    emphasis: {
      bold: {},
      subdued: {},
    },
    category: {
      one: {},
      two: {},
      three: {},
      four: {},
      neutral: {},
      success: {},
      warning: {},
      critical: {},
      informational: {},
    },
  },
  compoundVariants: [
    {
      emphasis: 'bold',
      category: 'one',
      css: {
        backgroundColor: '$tagOneBackgroundBold',
        borderColor: '$tagOneBorderBold',
        color: '$tagOneTextBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'two',
      css: {
        backgroundColor: '$tagTwoBackgroundBold',
        color: '$tagTwoTextBold',
        borderColor: '$tagTwoBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'three',
      css: {
        backgroundColor: '$tagThreeBackgroundBold',
        color: '$tagThreeTextBold',
        borderColor: '$tagThreeBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'four',
      css: {
        backgroundColor: '$tagFourBackgroundBold',
        color: '$tagFourTextBold',
        borderColor: '$tagFourBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'neutral',
      css: {
        backgroundColor: '$tagNeutralBackgroundBold',
        color: '$tagNeutralTextBold',
        borderColor: '$tagNeutralBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'success',
      css: {
        backgroundColor: '$tagSuccessBackgroundBold',
        color: '$tagSuccessTextBold',
        borderColor: '$tagSuccessBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'warning',
      css: {
        backgroundColor: '$tagWarningBackgroundBold',
        color: '$tagWarningTextBold',
        borderColor: '$tagWarningBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'critical',
      css: {
        backgroundColor: '$tagCriticalBackgroundBold',
        color: '$tagCriticalTextBold',
        borderColor: '$tagCriticalBorderBold',
      },
    },
    {
      emphasis: 'bold',
      category: 'informational',
      css: {
        backgroundColor: '$tagInformationalBackgroundBold',
        color: '$tagInformationalTextBold',
        borderColor: '$tagInformationalBorderBold',
      },
    },
    {
      emphasis: 'subdued',
      category: 'one',
      css: {
        backgroundColor: '$tagOneBackgroundSubdued',
        color: '$tagOneTextSubdued',
        borderColor: '$tagOneBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'two',
      css: {
        backgroundColor: '$tagTwoBackgroundSubdued',
        color: '$tagTwoTextSubdued',
        borderColor: '$tagTwoBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'three',
      css: {
        backgroundColor: '$tagThreeBackgroundSubdued',
        color: '$tagThreeTextSubdued',
        borderColor: '$tagThreeBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'four',
      css: {
        backgroundColor: '$tagFourBackgroundSubdued',
        color: '$tagFourTextSubdued',
        borderColor: '$tagFourBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'neutral',
      css: {
        backgroundColor: '$tagNeutralBackgroundSubdued',
        color: '$tagNeutralTextSubdued',
        borderColor: '$tagNeutralBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'success',
      css: {
        backgroundColor: '$tagSuccessBackgroundSubdued',
        color: '$tagSuccessTextSubdued',
        borderColor: '$tagSuccessBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'warning',
      css: {
        backgroundColor: '$tagWarningBackgroundSubdued',
        color: '$tagWarningTextSubdued',
        borderColor: '$tagWarningBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'critical',
      css: {
        backgroundColor: '$tagCriticalBackgroundSubdued',
        color: '$tagCriticalTextSubdued',
        borderColor: '$tagCriticalBorderSubdued',
      },
    },
    {
      emphasis: 'subdued',
      category: 'informational',
      css: {
        backgroundColor: '$tagInformationalBackgroundSubdued',
        color: '$tagInformationalTextSubdued',
        borderColor: '$tagInformationalBorderSubdued',
      },
    },
  ],
});

export const Tag: FC<GDSTagProps> = ({
  iconAltText,
  label,
  category = 'neutral',
  className,
  css,
  'data-testid': testId = `GDS-${DISPLAY_NAME}`,
  emphasis = 'bold',
  icon,
  iconTint,
  size = 'small',
  ...props
}: GDSTagProps) => (
  <GenesisCoreInspector displayName={DISPLAY_NAME}>
    <BaseTag
      category={category}
      className={['GDS-tag', className].join(' ')}
      css={css}
      data-testid={testId}
      emphasis={emphasis}
      orientation="horizontal"
      size={size}
      spacing="$quarter"
      verticalAlignment="center"
      {...props}
    >
      {icon && (
        <Icon
          className="GDS-tag-icon"
          css={{ flexShrink: 0 }}
          icon={icon}
          size={size === 'medium' ? '$one' : '$threeQuarters'}
          tint={iconTint}
          label={iconAltText}
        />
      )}

      <StackItem css={{ flexWrap: 'wrap' }} as="span" className="GDS-tag-label">
        {label}
      </StackItem>
    </BaseTag>
  </GenesisCoreInspector>
);

Tag.displayName = DISPLAY_NAME;
